import { useCallback, useMemo } from "react";
import {
  getTravellersList,
  getBaggagesList,
  getExtraBaggagesList,
  getTravellersTypes,
  getStatus,
  getDetailedStatus,
  BaggagesDetailsBySegment
} from "./utils";
import { useSelector } from "react-redux";
import { getExtraProducts, getExtraProductsFetchStatus } from "ancillaries/store/extraProducts/selectors";
import { getFlight } from "store/flightDetails/selectors";

export default function useBaggages() {
  const flight = useSelector(getFlight);
  const extraProductsFetchStatus = useSelector(getExtraProductsFetchStatus);
  const extraProducts = useSelector(getExtraProducts);

  const {
    travellers = [],
    travellerPrices = [],
    ancillaries = {},
    includedProductsBySegment = [],
    segments = []
  } = flight || {};

  const travellersList = useMemo(() => {
    return getTravellersList(travellers.length ? travellers : travellerPrices.length ? travellerPrices : []);
  }, [travellers, travellerPrices]);

  const travellerTypes = useMemo(() => {
    return getTravellersTypes(travellersList);
  }, [travellersList]);

  const baggagesList = useMemo(() => {
    return getBaggagesList({ segments, travellersList, includedProductsBySegment });
  }, [segments, travellersList, includedProductsBySegment]);

  const extraBaggagesList = useMemo(() => {
    /**
     We must use `store.extraProducts` (instead of `flight.ancillaries`) on checkout steps.
     In Branded Fares scenario `flight.ancillaries` always contains ancillaries of a base fare, while non-base fare
     could be selected by user which has different set of ancillaries =>
     `store.extraProducts` is the source of ancillaries truth during checkout.
     */
    const ancillariesToUse = extraProductsFetchStatus === "success" ? extraProducts : ancillaries;

    return getExtraBaggagesList({
      ancillaries: ancillariesToUse || {},
      travellersList
    });
  }, [extraProductsFetchStatus, extraProducts, ancillaries, travellersList]);

  const getBaggagesStatus = useCallback(() => {
    return getStatus({ segments, travellerTypes, baggagesList, extraBaggagesList });
  }, [segments, travellerTypes, baggagesList, extraBaggagesList]);

  const getBaggageDetails = useCallback(() => {
    return getDetailedStatus({ segments, travellerTypes, baggagesList, extraBaggagesList });
  }, [segments, travellerTypes, baggagesList, extraBaggagesList]);

  const getBaggageDetailsByTravellerReference = useCallback(
    (ref: string): BaggagesDetailsBySegment | undefined => {
      const traveller = travellersList.find((_) => _.travellerReference === ref);
      if (!traveller) return undefined;
      const baggageDetails = getDetailedStatus({ segments, travellerTypes, baggagesList, extraBaggagesList });
      return baggageDetails[traveller.travellerType];
    },
    [segments, travellersList, travellerTypes, baggagesList, extraBaggagesList]
  );

  return { getBaggagesStatus, getBaggageDetails, getBaggageDetailsByTravellerReference };
}
